import * as _ from 'lodash';
import { claimStates } from '../objects/Claim';
import { PolitikerCalculator } from './calculation/PolitikerCalculator';
import { StandardCalculator } from './calculation/StandardCalculator';
import { PayslipModel } from './PayslipModel';

/**
 * @typedef { import('../objects/Payment').Payment } Payment
 * @typedef { import('../objects/Meeting').Meeting } Meeting
 * @typedef { import('../objects/Attendance').Attendance } Attendance
 * @typedef { import('../objects/Payslip').Payslip } Payslip
 * @typedef { import('../objects/WageCalculation').WageCalculation } WageCalculation
 * @typedef { import('../objects/PolitikerSettings').PolitikerSettings } PolitikerSettings
*/

export class PayslipCollection {
    /**
     * @param {Meeting} meeting
     * @param {Payslip[]} payslips
     * @param {PolitikerSettings[]} politikerSettings
     * @param {WageCalculation} meetingWageCalculation
     */
    constructor(meeting, payslips, politikerSettings, meetingWageCalculation) {
        this.hasChanges = false;

        let politikerCalculator = null;

        this.isMissingPolitiker = false;
        if (politikerSettings != null && politikerSettings.length > 0) {
            politikerCalculator = PolitikerCalculator.forDate(meeting.Date, politikerSettings);
            if (politikerCalculator == null) {
                this.isMissingPolitiker = _.some(payslips, r => _.some(r.Claims, c => c.State === claimStates.reported));
            }
        }

        let meetingCalculator = null;
        if (meetingWageCalculation != null) {
            meetingCalculator = new StandardCalculator(meeting, meetingWageCalculation);
        }

        let models = _.map(payslips, x => new PayslipModel(this, x, politikerCalculator, meetingCalculator));
        this.payslipModels = PayslipModel.order(models);
        _.forEach(this.payslipModels, (x, i) => x.$$order = i);

        this.hasAnyTransactionIssues = this.payslipModels.some(x => x.hasTransactionIssues);
        this.hasAnyWarnings = this.payslipModels.some(x => x.hasWarnings);
        this.hasAnyMissingMeetingPayment = this.payslipModels.some(x => x.isMissingMeetingPayment);
        this.hasAnyMissingCertifier = this.payslipModels.some(x => x.isMissingCertifier);
        this.hasAnyPolitikerTasks = this.payslipModels.some(x => x.hasPolitikerTasks);
        this.hasAnyRejectedCertification = this.payslipModels.some(x => x.hasRejectedCertification);
        this.hasAnyRecalledCertification = this.payslipModels.some(x => x.hasRecalledCertification);
    }

    /**
     * @param {WageCalculation} wageCalculation
     * @param {(x:PayslipModel)=>boolean} payslipFilter
     */
    calculate(wageCalculation, payslipFilter) {
        if (this.payslipModels == null) return;
        if (this.payslipModels.length === 0) return;

        let calculator = new StandardCalculator(this.payslipModels[0].attendance.Meeting, wageCalculation);
        for (let rem of this.payslipModels) {
            if (payslipFilter == null || payslipFilter(rem)) {
                rem.recalculate(calculator);
            }
        }
    }

    /**
     * @param {PayslipModel} payslipModel
     */
    payslipChanged(payslipModel) {
        this.hasChanges = payslipModel.needsSaving || _.some(this.payslipModels, x => x.needsSaving);
        this.hasAnyMissingMeetingPayment = payslipModel.isMissingMeetingPayment || _.some(this.payslipModels, x => x.isMissingMeetingPayment);
        this.hasAnyMissingCertifier = payslipModel.isMissingCertifier || _.some(this.payslipModels, x => x.isMissingCertifier);
        this.hasAnyPolitikerTasks = payslipModel.hasPolitikerTasks || _.some(this.payslipModels, x => x.hasPolitikerTasks);
        this.hasAnyRejectedCertification = payslipModel.hasRejectedCertification || _.some(this.payslipModels, x => x.hasRejectedCertification);
        this.hasAnyRecalledCertification = payslipModel.hasRecalledCertification || _.some(this.payslipModels, x => x.hasRecalledCertification);
    }

    /**
     */
    countSelectedPayments() {
        return _.sumBy(this.payslipModels, rem => _.sumBy(rem.paymentGroups, pg => _.sumBy(pg.payments, p => p.$$selected ? 1 : 0)));
    }

    /**
     */
    removeSelectedPayments() {
        for (let rem of this.payslipModels) {
            rem.removeMatchingPayments(x => x.$$selected);
        }
    }

    approveAllPolitiker() {
        for (let rem of this.payslipModels) {
            rem.approveAllPolitiker();
        }
    }

    createUpdate() {
        let changed = _.filter(this.payslipModels, x => x.needsSaving);
        return {
            Updated: _.map(changed, x => x.createUpdate()),
        };
    }
}